// src/features/portfolio-optimization/api.ts
//
// Data fetcher for the Portfolio Optimizer "Glass-Box" dashboard. Talks to
// the FastAPI portfolio router via the shared `apiClient` axios instance,
// which attaches the WordPress JWT and already bakes the `/api/v1` version
// prefix into its baseURL — so we request the bare `/portfolio/matrix` path
// here (adding `/api/v1` would double the prefix and 404).
//
// The endpoint returns the target weights for ALL risk profiles in one
// payload. The page pre-fetches once and the risk slider recomputes the
// charts/tables client-side, so dragging the slider never hits the network.

import { apiClient } from '../../api/client';

export interface PortfolioAsset {
    symbol: string;
    company_name: string;
    sector: string;
    /** Target weight as a fraction in [0, 1] (NOT percent). */
    target_weight: number;
    /** Phase-1 composite conviction z-score; null when no ranking joins. */
    forge_ultra_signal: number | null;
    /** 90-day annualised volatility as a fraction; null when absent. */
    annualized_volatility: number | null;
}

export interface RiskProfileAllocations {
    risk_profile: string;
    /** Holdings, pre-sorted server-side by descending target weight. */
    assets: PortfolioAsset[];
}

export interface PortfolioMatrixResponse {
    status: string;
    /** ISO date string (YYYY-MM-DD) of the rebalance date served. */
    target_date: string;
    domain: string;
    /** Risk-profile name -> its allocation block. */
    profiles: Record<string, RiskProfileAllocations>;
}

export const fetchPortfolioMatrix = async (
    domain = 'stock_ranking',
): Promise<PortfolioMatrixResponse> => {
    // Resolves to GET /api/v1/portfolio/matrix (prefix from baseURL).
    const { data } = await apiClient.get<PortfolioMatrixResponse>(
        '/portfolio/matrix',
        { params: { domain } },
    );
    return data;
};

// --- ML "Quant X-Ray" -----------------------------------------------------
//
// The ML insights endpoint exposes the Black-Litterman *views* the meta-model
// formed for each symbol on the rebalance date — the calibrated probability,
// the view return Q, the view uncertainty Omega and the Phase-1 z-score. These
// are domain-level (NOT per risk profile), so the page fetches them once
// alongside the matrix.

export interface MlInsightSignal {
    symbol: string;
    /** Primary bet direction: +1 (long) or -1 (short). */
    ml_side: number;
    /** Stacked, calibrated probability the primary side is correct (0..1). */
    master_probability: number;
    /** Black-Litterman view return Q (0.0 when edge is non-positive). */
    expected_return_q: number;
    /** Diagonal of the view-confidence matrix Omega (large = low confidence). */
    uncertainty_omega: number;
    /** Phase-1 composite z-score that triggered the bet; null when absent. */
    phase_1_z_score: number | null;
}

export interface MlInsightsResponse {
    status: string;
    /** ISO date string (YYYY-MM-DD) of the rebalance date served. */
    target_date: string;
    domain: string;
    active_views_count: number;
    /** Views, pre-sorted server-side by descending master_probability. */
    signals: MlInsightSignal[];
}

export const fetchMlInsights = async (
    domain = 'stock_ranking',
): Promise<MlInsightsResponse> => {
    // Resolves to GET /api/v1/portfolio/ml-insights (prefix from baseURL).
    const { data } = await apiClient.get<MlInsightsResponse>(
        '/portfolio/ml-insights',
        { params: { domain } },
    );
    return data;
};

// --- Tactical Stock Recommendations (Zone 4) -----------------------------
//
// Fetches the daily tactical multi-horizon recommendation matrix from the
// ML inference engine. Each recommendation exposes adaptive Buy/Sell/Hold
// actions, probability distributions, expected returns, and triple-barrier
// price guidance bands across three horizons (21/45/60 days).

export type ActionType = 'BUY' | 'SELL' | 'HOLD';

export interface PriceGuidanceBand {
    buy_below: number | null;
    sell_above: number | null;
    stop: number | null;
}

export interface QualityMetrics {
    forge_ultra_signal: number;
    annualized_volatility: number;
    liquidity_amihud: number | null;
    macro_alignment: string | null;
}

export interface StockRecommendation {
    symbol: string;
    company_name: string | null;
    sector: string | null;
    /** Directional bet: +1 (long) or -1 (short). */
    side: number;
    /** Calibrated probabilities per horizon: "H21", "H45", "H60", "master". */
    probabilities: Record<string, number>;
    /** Parameterised expected returns per horizon (fractional, NOT percent). */
    expected_returns: Record<string, number>;
    /** Recommended action per horizon: adaptive threshold logic. */
    recommended_action: Record<string, ActionType>;
    /** Triple-barrier price guidance per horizon. */
    price_guidance: Record<string, PriceGuidanceBand>;
    /** Phase-1 quality metrics. */
    quality: QualityMetrics;
}

export interface StockRecommendationResponse {
    /** ISO date string (YYYY-MM-DD) of the target date. */
    target_date: string;
    domain: string;
    /** All recommendations for the universe, unsorted. */
    recommendations: StockRecommendation[];
}

export const fetchStockRecommendations = async (
    domain = 'stock_ranking',
): Promise<StockRecommendationResponse> => {
    // Resolves to GET /api/v1/stock-recommendations (prefix from baseURL).
    const { data } = await apiClient.get<StockRecommendationResponse>(
        '/stock-recommendations',
        { params: { domain } },
    );
    return data;
};
